import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';

import {
  Title as TitleComponent,
  Description as DescriptionComponent,
  BaseLinkStyle,
  BaseWhiteLinkStyle,
} from './ContentSlice';
import { normaIcons } from './benefit-icons';

import { fieldHasContent, transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, mobileBreakpoint } from '@/src/domains/shared/breakpoints';

const Section = styled.section`
  ${fluidProp('padding-bottom', '40px', '80px')}
  ${fluidProp('padding-top', '90px', '150px')}
  font-family: var(--typography-fontBrand);
  background-color: var(--newBrandColor);
  color: var(--colors-blank);
  position: relative;
`;

export const Description = styled(DescriptionComponent)`
  && {
    color: var(--colors-blank);
    ${BaseWhiteLinkStyle}

    > b,
    strong {
      ${fluidProp('font-size', '20px', '32px')}
    }

    > p {
      margin-bottom: 20px;
    }

    > ul,
    ol {
      padding-left: 0;
      list-style: none;
      line-height: 1.7;
      margin-top: 50px;

      b,
      strong {
        ${fluidProp('font-size', '18px', '22px')}
      }

      li {
        margin-bottom: 30px;
        position: relative;
      }
    }

    > ul {
      li::before {
        content: '';
        width: 100px;
        height: 1px;
        top: 15px;
        left: -120px;
        background-color: #a2eb00;

        margin-right: 10px;
        position: absolute;
      }
    }

    > ol {
      li::before {
        content: '';
        width: 5px;
        height: 5px;
        border-radius: 3px;
        top: 14px;
        left: -30px;
        background-color: var(--colors-blank);
        margin-right: 10px;
        position: absolute;
      }
    }
  }

  h2 {
    font-weight: 700;
    ${fluidProp('font-size', '38px', '48px')}
    line-height: 113.5%;
    color: var(--colors-blank);
    padding: 30px 0;

    > * {
      font-size: inherit;
      line-height: inherit;
      font-weight: inherit;
      margin: 0;
    }
  }
`;

export const Title = styled(TitleComponent)`
  color: var(--colors-blank);
  max-width: 500px;
`;

const Subtitle = styled.div`
  ${({ theme }) => theme.typography.titleLG}
  max-width: 530px;
  line-height: 1.5;
  margin-top: 30px;
`;

const Content = styled.div`
  display: flex;
  justify-content: space-between;

  ${tabletBreakpoint(css`
    flex-direction: column-reverse;
  `)};
`;

const TceWrapper = styled.div`
  background-color: var(--colors-blank);
  color: rgba(20, 25, 30, 0.85);
  border-radius: 15px;
  padding: 65px 50px 33px;
  position: relative;
  margin: 60px 0;

  ${tabletBreakpoint(css`
    padding: 45px 35px 25px;
  `)};

  ${mobileBreakpoint(css`
    padding-left: 25px;
    padding-right: 25px;
  `)};
`;

const TceDescription = styled(DescriptionComponent)`
  ${BaseLinkStyle}
  color: rgba(20, 25, 30, 0.85);
  padding: 0;

  ul {
    padding: 15px 0 15px 25px;
  }

  h2 {
    color: #14191e;
    ${({ theme }) => theme.typography.scale3};
    margin-bottom: 20px;
  }

  p {
    padding-bottom: 15px;
  }
`;

const HalfCircle = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 150%;
  background-color: var(--newBrandColor);
  top: -3%;
  left: -7%;

  ${tabletBreakpoint(css`
    display: none;
  `)};
`;

const CalendarIconWrapper = styled.div`
  position: absolute;
  top: -20px;
  left: 40px;
  z-index: 1;
  background-color: var(--colors-blank);
  padding: 15px;
  box-sizing: border-box;
  border-radius: 12px;
  color: var(--newBrandColor);

  svg {
    height: 35px;
    width: 35px;
  }
`;

const CalendarIcon = normaIcons['calendar-dates'];

export function CountryContentSlice({ slice }) {
  const { primary, context } = transformProperties(slice);

  const { title, subtitle, description, flag, map, tceSection, additionalDescription, sectionId } =
    primary;
  const { countryName } = context;
  const defaultTitle = `Understanding Hiring in ${countryName}`;

  return (
    <Section id={sectionId}>
      <ContentContainer>
        <Content>
          <div>
            {flag && (
              <Image src={flag.url} alt={flag.alt || ''} width={45} height={45} loading="eager" />
            )}

            {fieldHasContent(title) ? (
              <Title>
                <PrismicRichText field={title} />
              </Title>
            ) : (
              <Title>{defaultTitle}</Title>
            )}

            {fieldHasContent(subtitle) && (
              <Subtitle>
                <PrismicRichText field={subtitle} />
              </Subtitle>
            )}
          </div>
          {map && (
            <Image
              src={map.url}
              alt={map.alt || ''}
              width={map.dimensions.width}
              height={map.dimensions.height}
              quality={100}
              loading="eager"
            />
          )}
        </Content>

        {fieldHasContent(description) && (
          <Description>
            <PrismicRichText field={description} />
          </Description>
        )}

        {fieldHasContent(tceSection) && (
          <TceWrapper>
            <TceDescription>
              <PrismicRichText field={tceSection} />
            </TceDescription>
            <CalendarIconWrapper>
              <CalendarIcon />
            </CalendarIconWrapper>
          </TceWrapper>
        )}

        {fieldHasContent(additionalDescription) && (
          <Description>
            <PrismicRichText field={additionalDescription} />
          </Description>
        )}
      </ContentContainer>
      <HalfCircle />
    </Section>
  );
}
